import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ApiUrl } from "../../../ApiUrl";
import "./AdmFeeReport.css";

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatAmount = (value) => toNumber(value).toLocaleString("en-IN");

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const isDiscountElement = (elementName) =>
  normalizeText(elementName).includes("discount");

const getUniqueOrderedValues = (items) => [...new Set(items.filter(Boolean))];

const feePeriodOrder = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
  yearly: 13,
};

const getFeePeriodSortKey = (period) => {
  const normalizedPeriod = normalizeText(period);
  const isYearlyPeriod = /\s*\(yearly\)$/.test(normalizedPeriod);
  const basePeriod = normalizedPeriod
    .replace(/\s*\(yearly\)$/, "")
    .trim();

  return {
    order: feePeriodOrder[basePeriod] || Number.MAX_SAFE_INTEGER,
    yearly: isYearlyPeriod ? 1 : 0,
  };
};

const compareFeePeriods = (firstPeriod, secondPeriod) => {
  const firstSortKey = getFeePeriodSortKey(firstPeriod);
  const secondSortKey = getFeePeriodSortKey(secondPeriod);
  const orderDifference = firstSortKey.order - secondSortKey.order;

  if (orderDifference !== 0) return orderDifference;
  if (firstSortKey.yearly !== secondSortKey.yearly) {
    return firstSortKey.yearly - secondSortKey.yearly;
  }

  return String(firstPeriod || "").localeCompare(String(secondPeriod || ""));
};

const AdmFeeReport = () => {
  const [academicSessionId, setAcademicSessionId] = useState("");
  const [academicSessionCode, setAcademicSessionCode] = useState("");
  const [classOptions, setClassOptions] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [studentCards, setStudentCards] = useState([]);
  const [loading, setLoading] = useState(false);

  const syncAcademicContext = useCallback(() => {
    const sessionId = localStorage.getItem("academicSessionId") || "";
    const sessionCode =
      localStorage.getItem("academicSessionCode") ||
      localStorage.getItem("sessionCode") ||
      "";

    setAcademicSessionId(sessionId);
    setAcademicSessionCode(sessionCode || sessionId);
  }, []);

  useEffect(() => {
    syncAcademicContext();
    window.addEventListener("academicSessionChanged", syncAcademicContext);

    return () => {
      window.removeEventListener("academicSessionChanged", syncAcademicContext);
    };
  }, [syncAcademicContext]);

  useEffect(() => {
    const fetchClassData = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}Class/GetAllClass`);
        const data = await response.json();

        if (String(data?.message || "").toLowerCase().includes("success")) {
          setClassOptions(
            (data?.data || []).map((classItem) => ({
              label: classItem.classname,
              value: classItem.id,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching classes:", error);
      }
    };

    fetchClassData();
  }, []);

  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}Periods/GetAllPeriods/`);
        const data = await response.json();

        if (Array.isArray(data?.data)) {
          setSessionOptions(
            data.data.map((period) => ({
              label: period.period_name,
              value: period.id,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };

    fetchPeriods();
  }, []);

  const buildStudentFeeView = useCallback((feesDetails, selectedPeriodName) => {
    const filteredRows = (feesDetails || [])
      .filter((item) => {
        if (!selectedPeriodName) {
          return true;
        }

        return normalizeText(item.periodName) === normalizeText(selectedPeriodName);
      })
      .map((item) => {
        const totalAmount = toNumber(item.total_amount);
        const paidAmount = toNumber(item.paid_amount);
        const balanceAmount = toNumber(
          item.remaining_amount ?? totalAmount - paidAmount
        );

        return {
          periodName: item.periodName || "-",
          elementName: item.element_name || "-",
          totalAmount,
          paidAmount,
          balanceAmount,
          isDiscount: isDiscountElement(item.element_name),
        };
      })
      .sort((first, second) =>
        compareFeePeriods(first.periodName, second.periodName)
      );

    const groupedByMonth = filteredRows.reduce((acc, row) => {
      const key = row.periodName || "-";

      if (!acc[key]) {
        acc[key] = {
          periodName: key,
          rows: [],
          elements: [],
          totalAmount: 0,
          paidAmount: 0,
          discountAmount: 0,
          balanceAmount: 0,
        };
      }

      if (row.isDiscount) {
        acc[key].discountAmount += row.totalAmount || row.paidAmount;
      } else {
        acc[key].rows.push(row);
        acc[key].elements.push(row.elementName);
        acc[key].totalAmount += row.totalAmount;
        acc[key].paidAmount += row.paidAmount;
        acc[key].balanceAmount += row.balanceAmount;
      }

      return acc;
    }, {});

    const monthGroups = Object.values(groupedByMonth)
      .map((group) => ({
        ...group,
        elements: getUniqueOrderedValues(group.elements),
      }))
      .sort((first, second) =>
        compareFeePeriods(first.periodName, second.periodName)
      );

    const totals = filteredRows.reduce(
      (acc, row) => {
        if (row.isDiscount) {
          acc.discount += row.totalAmount || row.paidAmount;
        } else {
          acc.totalFees += row.totalAmount;
          acc.feesPaid += row.paidAmount;
        }
        acc.balance += row.balanceAmount;
        return acc;
      },
      {
        totalFees: 0,
        feesPaid: 0,
        discount: 0,
        balance: 0,
      }
    );

    return {
      detailRows: filteredRows,
      monthGroups,
      ...totals,
    };
  }, []);

  const fetchReportData = useCallback(
    async (classFilter = selectedClass, sessionFilter = selectedSession) => {
      if (!academicSessionId) {
        alert("Academic session is not selected from navbar.");
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams({
          academic_year_id: academicSessionId,
          showfees: "F",
        });

        if (classFilter?.value) {
          params.append("classId", classFilter.value);
        }

        const studentListResponse = await fetch(
          `${ApiUrl.apiurl}FEE_LEDGER/GetFEELEDGERBASEDONCONDITION/?${params.toString()}`
        );
        const studentListPayload = await studentListResponse.json();
        const students = Array.isArray(studentListPayload?.data)
          ? studentListPayload.data
          : [];

        const cards = await Promise.all(
          students.map(async (student) => {
            try {
              const detailsResponse = await fetch(
                `${ApiUrl.apiurl}FEE_LEDGER/GetStudentFeesDetailsPDFBasedOnStudentId/${academicSessionId}/${student.studentId}/`
              );
              const detailsPayload = await detailsResponse.json();
              const data = detailsPayload?.data || {};

              const shaped = buildStudentFeeView(
                data.feesdetails || [],
                sessionFilter?.label || ""
              );

              if (!shaped.detailRows.length) {
                return null;
              }

              return {
                studentId: student.studentId,
                studentName: student.studentname || "-",
                className: student.classname || "-",
                sectionName: student.sectionname || "-",
                schoolAdmissionNo: student.school_admission_no || "-",
                fatherName: student.fatherName || "-",
                motherName: student.motherName || data.mothername || "-",
                ...shaped,
              };
            } catch (error) {
              console.error("Error fetching student fee details:", error);
              return null;
            }
          })
        );

        setStudentCards(cards.filter(Boolean));
      } catch (error) {
        console.error("Error loading fee report:", error);
        setStudentCards([]);
        alert("Failed to load fee report data.");
      } finally {
        setLoading(false);
      }
    },
    [academicSessionId, buildStudentFeeView, selectedClass, selectedSession]
  );

  const handleSearch = useCallback(async () => {
    if (!academicSessionId) {
      alert("Academic session is not selected from navbar.");
      return;
    }

    await fetchReportData(selectedClass, selectedSession);
  }, [academicSessionId, fetchReportData, selectedClass, selectedSession]);

  useEffect(() => {
    if (academicSessionId) {
      fetchReportData();
    }
  }, [academicSessionId, fetchReportData]);

  const handleClear = () => {
    setSelectedClass(null);
    setSelectedSession(null);
    if (academicSessionId) {
      fetchReportData(null, null);
    } else {
      setStudentCards([]);
    }
  };

  const exportToExcel = () => {
    if (!studentCards.length) {
      alert("No data to export");
      return;
    }

    const rows = studentCards.flatMap((card, index) =>
      card.detailRows.map((row) => ({
        "Sr No": index + 1,
        Session: academicSessionCode || academicSessionId,
        Class: card.className,
        Section: card.sectionName,
        "Student Name": card.studentName,
        "Admission No": card.schoolAdmissionNo,
        "Father Name": card.fatherName,
        "Mother Name": card.motherName,
        Month: row.periodName,
        "Fee Element": row.elementName,
        "Total Amount": row.totalAmount,
        "Paid Amount": row.paidAmount,
        Balance: row.balanceAmount,
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Report");
    const output = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([output], { type: "application/octet-stream" }), "FeeReport.xlsx");
  };

  const handleClose = () => {
    window.history.back();
  };

  const summary = useMemo(
    () =>
      studentCards.reduce(
        (acc, card) => {
          acc.totalFees += card.totalFees;
          acc.totalPaid += card.feesPaid;
          acc.totalDiscount += card.discount;
          acc.totalBalance += card.balance;
          return acc;
        },
        {
          totalFees: 0,
          totalPaid: 0,
          totalDiscount: 0,
          totalBalance: 0,
        }
      ),
    [studentCards]
  );

  return (
    <div className="container-fluid fee-report-page">
      <div className="row">
        <div className="col-12">
          <div className="card fee-report-card">
            <div className="card-body">
              <h2 className="fee-report-title">FEE REPORT</h2>

              <div className="row mb-3 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    className="btn btn-primary"
                    style={{ width: "120px" }}
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Search"}
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ width: "120px" }}
                    onClick={handleClear}
                    disabled={loading}
                  >
                    Clear
                  </button>
                  <button
                    className="btn btn-danger"
                    style={{ width: "120px" }}
                    onClick={handleClose}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-success"
                    style={{ width: "150px" }}
                    onClick={exportToExcel}
                    disabled={loading || !studentCards.length}
                  >
                    Export to Excel
                  </button>
                </div>
              </div>

              <div className="row mb-2 mx-1 align-items-end">
                <div className="col-md-6 mb-3">
                  <label className="fee-report-label">Class</label>
                  <Select
                    value={selectedClass}
                    onChange={setSelectedClass}
                    options={classOptions}
                    classNamePrefix="react-select"
                    isClearable
                    placeholder="Select Class"
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="fee-report-label">Session</label>
                  <Select
                    value={selectedSession}
                    onChange={setSelectedSession}
                    options={sessionOptions}
                    classNamePrefix="react-select"
                    isClearable
                    placeholder="Select Session"
                  />
                </div>
              </div>

              {loading && <div className="fee-report-empty">Loading student fee report...</div>}

              {!loading && !studentCards.length && (
                <div className="fee-report-empty">
                  No fee details found for the selected filters.
                </div>
              )}

              {!loading &&
                studentCards.map((card, index) => (
                  <div className="student-report-block" key={card.studentId}>
                    <div className="student-report-head">
                      <div className="student-report-left">
                        <span className="student-index">
                          {index + 1}. {card.studentName}
                        </span>
                        <span className="student-badge">{academicSessionCode || academicSessionId}</span>
                        <span className="student-badge">{card.className}</span>
                        <span className="student-badge">{card.sectionName}</span>
                      </div>

                      <div className="student-report-right">
                        <span>
                          <strong>Father:</strong> {card.fatherName}
                        </span>
                        <span>
                          <strong>Mother:</strong> {card.motherName}
                        </span>
                      </div>
                    </div>

                    <div className="table-responsive student-summary-table-wrap">
                      <table className="table table-bordered student-summary-table">
                        <thead>
                          <tr>
                            {card.monthGroups.map((group) => (
                              <th
                                key={`${card.studentId}-${group.periodName}-month`}
                                colSpan={Math.max(group.elements.length, 1) * 3}
                              >
                                {group.periodName}
                              </th>
                            ))}
                            <th rowSpan="2">Total Fees</th>
                            <th rowSpan="2">Fees Paid</th>
                            <th rowSpan="2">Discount</th>
                            <th rowSpan="2">Balance</th>
                          </tr>
                          <tr>
                            {card.monthGroups.map((group) =>
                              (group.elements.length ? group.elements : ["No Elements"]).map((element) => (
                                <React.Fragment
                                  key={`${card.studentId}-${group.periodName}-${element}-header`}
                                >
                                  <th>{element} Total</th>
                                  <th>{element} Paid</th>
                                  <th>{element} Bal</th>
                                </React.Fragment>
                              ))
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="student-total-row">
                            {card.monthGroups.map((group) => {
                              const elements = group.elements.length
                                ? group.elements
                                : ["No Elements"];

                              return elements.map((element) => {
                                const elementRows = group.rows.filter(
                                  (row) => row.elementName === element
                                );
                                const totalAmount = elementRows.reduce(
                                  (sum, row) => sum + row.totalAmount,
                                  0
                                );
                                const paidAmount = elementRows.reduce(
                                  (sum, row) => sum + row.paidAmount,
                                  0
                                );
                                const balanceAmount = elementRows.reduce(
                                  (sum, row) => sum + row.balanceAmount,
                                  0
                                );

                                return (
                                  <React.Fragment
                                    key={`${card.studentId}-${group.periodName}-${element}-value`}
                                  >
                                    <td>{formatAmount(totalAmount)}</td>
                                    <td>{formatAmount(paidAmount)}</td>
                                    <td>{formatAmount(balanceAmount)}</td>
                                  </React.Fragment>
                                );
                              });
                            })}
                            <td>{formatAmount(card.totalFees)}</td>
                            <td>{formatAmount(card.feesPaid)}</td>
                            <td>{formatAmount(card.discount)}</td>
                            <td>{formatAmount(card.balance)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                  </div>
                ))}

              {!!studentCards.length && (
                <div className="fee-report-summary mt-3">
                  <div>
                    <strong>Total Students:</strong> {studentCards.length}
                  </div>
                  <div>
                    <strong>Total Fees:</strong> {formatAmount(summary.totalFees)}
                  </div>
                  <div>
                    <strong>Total Paid:</strong> {formatAmount(summary.totalPaid)}
                  </div>
                  <div>
                    <strong>Total Discount:</strong> {formatAmount(summary.totalDiscount)}
                  </div>
                  <div>
                    <strong>Total Balance:</strong> {formatAmount(summary.totalBalance)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmFeeReport;
